import { PermissionName } from '@sironamedical/common';
import { AppRoutePath, NOT_FOUND_CASE_ID } from 'config/constants';
import { useStudies } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useManagePatientJacket } from 'hooks/useManagePatientJacket';
import { useOpenTabs } from 'hooks/useOpenTabs';
import { useUrlBuilder } from 'hooks/useUrlBuilder';
import { usePopupOptionsGetter } from 'hooks/useWindowsRects';
import { WithApolloContext } from 'modules/Apollo/types';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { useMemo } from 'react';
import { getPatientJacketBasePath } from 'utils/urls';
import Search from '@mui/icons-material/Search';
import ListIcon from '@mui/icons-material/List';
import SvgIcon from '@mui/material/SvgIcon';
import ViewIcon from '@mui/icons-material/DesktopWindowsOutlined';
import SMSIcon from '@mui/icons-material/Sms';
import { ReactComponent as ReporterIconNav } from 'assets/reporterIconNav.svg';
import { ReactComponent as PriorStudiesIcon } from 'assets/priorStudies.svg';
import { useRecoilValue } from 'recoil';
import { isReporterOnlyExternalLaunchSelector } from 'common/External/state';
import { generatePath } from 'react-router-dom';
import { ViewerIconContainer, ViewerIndex } from './styles';
import SupervisorAccount from '@mui/icons-material/SupervisorAccount';
import { DEFAULT_CLUSTER_ADMIN_URLS } from 'config/index';
import { useValidateUserHasRbacPermissions } from 'hooks/useValidateUserHasRbacPermissions';
import { openPopup } from 'utils/openPopup';

const NAV_ICON_SIZE = 24;

export interface NavMenuItemConfig {
  /**
   * The label for the menu item, used for display and accessibility purposes.
   */
  label: string;

  /**
   * A unique identifier used for testing purposes. Should be unique across menu items to ensure tests can reliably select them.
   */
  dataTestId: string;

  /**
   * The base path that corresponds to this menu item. This is used to determine if the menu item should be highlighted as active based on the current URL. It can include dynamic segments (e.g., '/viewer/:windowId') which will be ignored when matching against the current path.
   */
  basePath: string;

  /**
   * The path to open when the menu item is clicked. If the menu item corresponds to a window that can be opened or focused, this should be the full path to that window (e.g., '/case/123/viewer/0'). If not provided, clicking the menu item will not trigger navigation, but it can still be used for other actions via the onClick handler.
   */
  openPath?: string;

  /**
   * The index of the viewer this menu item corresponds to, if applicable. At the moment, we support 0 and 1.
   */
  viewerIndex?: string;

  /**
   * What is rendered inside the menu item. This is typically an icon.
   */
  children: React.ReactNode;

  /**
   * Whether this menu item should open in an external tab or window.
   */
  isExternal?: boolean;

  /**
   * Whether the menu item should be disabled.
   */
  disabled?: boolean;

  /**
   * The tooltip to show when hovering over the menu item. If not provided, the tooltip will show the label.
   */
  tooltip?: string;

  /**
   * An optional click handler for the menu item. If provided, this will be called when the menu item is clicked. This takes precedent over openPath.
   */
  onClick?: () => void;
}

type ViewerItemArgs = {
  viewerId: '0' | '1';
  isBlank: boolean;
  currentCaseId: Nullable<string>;
  priorStudiesIds: string[] | Readonly<string[]>;
  autoloadedStudiesIds: string[] | Readonly<string[]>;
  openedViewerIds: Set<string>;
  buildViewerUrl: ReturnType<typeof useUrlBuilder>;
};

const createViewerItem = ({
  viewerId,
  isBlank,
  currentCaseId,
  priorStudiesIds,
  autoloadedStudiesIds,
  openedViewerIds,
  buildViewerUrl,
}: ViewerItemArgs): NavMenuItemConfig => {
  const isViewer1 = viewerId === '0';
  const isOpen = openedViewerIds.has(viewerId);

  return {
    label: isViewer1 ? 'Open/Show Viewer 1' : isOpen ? 'Open/Show Viewer 2' : 'Open new viewer',
    dataTestId: `navbar-viewer-${Number(viewerId) + 1}`,
    basePath: (isBlank ? AppRoutePath.VIEWER_BLANK : AppRoutePath.VIEWER).replace(
      ':windowId',
      viewerId
    ),
    openPath:
      buildViewerUrl({
        caseSmid: currentCaseId,
        studySmids: priorStudiesIds,
        autoloadedStudySmids: autoloadedStudiesIds,
        viewerId,
      }) ?? generatePath(AppRoutePath.VIEWER_BLANK, { windowId: viewerId }),
    viewerIndex: viewerId,
    disabled: !isViewer1 && !openedViewerIds.has('0'),
    tooltip:
      !isViewer1 && !openedViewerIds.has('0')
        ? 'Please open Viewer 1 before opening a second viewer window.'
        : undefined,
    children: (
      <ViewerIconContainer>
        <ViewIcon sx={{ fontSize: NAV_ICON_SIZE }} />
        <ViewerIndex>{isViewer1 ? (isOpen ? 1 : '') : isOpen ? 2 : '+'}</ViewerIndex>
      </ViewerIconContainer>
    ),
  };
};

type PatientJacketItemArgs = {
  isJacketOpen: boolean;
  isPatientJacketDockedOpen: boolean;
  shouldPatientJacketBeDocked: boolean;
  isBlank: boolean;
  currentCaseId: Nullable<string>;
  buildJacketUrl: ReturnType<typeof useUrlBuilder>;
  managePatientJacket: ReturnType<typeof useManagePatientJacket>['managePatientJacket'];
};

const createPatientJacketItem = ({
  isJacketOpen,
  isPatientJacketDockedOpen,
  shouldPatientJacketBeDocked,
  isBlank,
  currentCaseId,
  buildJacketUrl,
  managePatientJacket,
}: PatientJacketItemArgs): NavMenuItemConfig => {
  const disabled = !isJacketOpen && shouldPatientJacketBeDocked && isBlank;

  return {
    label: 'Show Patient Jacket',
    dataTestId: 'navbar-patient-jacket',
    basePath: getPatientJacketBasePath(isPatientJacketDockedOpen, isBlank),
    openPath: isJacketOpen
      ? (currentCaseId != null &&
          buildJacketUrl({
            caseSmid: currentCaseId,
          })) ||
        AppRoutePath.WORKLIST
      : undefined,
    onClick: !isJacketOpen
      ? () => managePatientJacket({ nextCaseSmid: currentCaseId, forceOpen: true })
      : undefined,
    disabled,
    tooltip: disabled
      ? 'Docked patient jacket cannot be opened without an active case.'
      : undefined,
    children: <SvgIcon sx={{ fontSize: NAV_ICON_SIZE }} component={PriorStudiesIcon} />,
  };
};

export const useGenerateNavBarMenuConfig = ({
  context,
}: WithApolloContext): Array<NavMenuItemConfig> => {
  const [isClearPatientContextEnabled] = useFeatureFlagEnabled(FF.CLEAR_PATIENT_CONTEXT);
  const [isAISandboxEnabled] = useFeatureFlagEnabled(FF.URT_ENABLED);
  const [isSearchPageEnabled] = useFeatureFlagEnabled(FF.SEARCH_PAGE);

  const hasAdminAccess = useValidateUserHasRbacPermissions({
    permissionNames: [PermissionName.AccessAdminPanel],
  });

  const currentCaseId = useCurrentCaseId();
  const { priorStudiesIds, autoloadedStudiesIds } = useStudies({ context });
  const { managePatientJacket } = useManagePatientJacket({ context });
  const { openedViewerIds, isJacketOpen, isPatientJacketDockedOpen } = useOpenTabs();
  const getPopupOptions = usePopupOptionsGetter();
  const { data } = useCurrentUser({ context });
  const me = data?.me;

  const hasActiveCase = currentCaseId != null && currentCaseId !== NOT_FOUND_CASE_ID;
  const isBlank = isClearPatientContextEnabled && !hasActiveCase;
  const shouldPatientJacketBeDocked = useMemo(() => {
    return me?.worklistSettings?.isPatientJacketDocked ?? true;
  }, [me?.worklistSettings?.isPatientJacketDocked]);
  const buildViewerUrl = useUrlBuilder({ isViewer: true, isBlank });
  const buildReporterUrl = useUrlBuilder({ isReporter: true, isBlank });
  const buildDockedJacketUrl = useUrlBuilder({ isDockedJacket: true, isBlank });
  const buildUndockedJacketUrl = useUrlBuilder({ isUndockedJacket: true, isBlank });
  const buildAISandboxUrl = useUrlBuilder({ isAISandbox: true, isBlank });
  const buildJacketUrl = isPatientJacketDockedOpen ? buildDockedJacketUrl : buildUndockedJacketUrl;

  const isReporterOnlyExternal = useRecoilValue(isReporterOnlyExternalLaunchSelector);

  return useMemo(() => {
    const items: NavMenuItemConfig[] = [];

    if (isSearchPageEnabled) {
      items.push({
        label: 'Show Search',
        dataTestId: 'navbar-search',
        basePath: AppRoutePath.WORKLIST_SEARCH,
        openPath: AppRoutePath.WORKLIST_SEARCH,
        children: <Search sx={{ fontSize: NAV_ICON_SIZE }} />,
      });
    }

    items.push({
      label: 'Show Worklist',
      dataTestId: 'navbar-worklist',
      basePath: isPatientJacketDockedOpen ? AppRoutePath.PATIENT_JACKET : AppRoutePath.WORKLIST,
      openPath:
        (isPatientJacketDockedOpen &&
          currentCaseId != null &&
          buildDockedJacketUrl({
            caseSmid: currentCaseId,
          })) ||
        '/worklist',
      children: <ListIcon sx={{ fontSize: NAV_ICON_SIZE }} />,
    });

    items.push(
      createPatientJacketItem({
        isJacketOpen,
        isPatientJacketDockedOpen,
        shouldPatientJacketBeDocked,
        isBlank,
        currentCaseId,
        buildJacketUrl,
        managePatientJacket,
      })
    );

    if (!isReporterOnlyExternal) {
      items.push(
        createViewerItem({
          viewerId: '0',
          isBlank,
          currentCaseId,
          priorStudiesIds,
          autoloadedStudiesIds,
          openedViewerIds,
          buildViewerUrl,
        })
      );

      items.push(
        createViewerItem({
          viewerId: '1',
          isBlank,
          currentCaseId,
          priorStudiesIds,
          autoloadedStudiesIds,
          openedViewerIds,
          buildViewerUrl,
        })
      );
    }

    items.push({
      label: 'Open/Show Reporter',
      dataTestId: 'navbar-reporter',
      basePath: isBlank ? AppRoutePath.REPORTER_BLANK : AppRoutePath.REPORTER,
      openPath:
        (currentCaseId != null &&
          buildReporterUrl({
            caseSmid: currentCaseId,
            studySmids: priorStudiesIds,
            autoloadedStudySmids: autoloadedStudiesIds,
          })) ||
        AppRoutePath.REPORTER_BLANK,
      children: <SvgIcon sx={{ fontSize: NAV_ICON_SIZE }} component={ReporterIconNav} />,
    });

    if (isAISandboxEnabled) {
      items.push({
        label: 'Open Cortex',
        dataTestId: 'navbar-ai-sandbox',
        basePath: isBlank ? AppRoutePath.AI_SANDBOX_BLANK : AppRoutePath.AI_SANDBOX,
        openPath:
          (currentCaseId != null &&
            buildAISandboxUrl({
              caseSmid: currentCaseId,
              studySmids: priorStudiesIds,
              autoloadedStudySmids: autoloadedStudiesIds,
            })) ||
          AppRoutePath.AI_SANDBOX_BLANK,
        children: <SMSIcon />,
      });
    }

    if (hasAdminAccess) {
      items.push({
        label: 'System Administration',
        dataTestId: 'navbar-admin',
        basePath: `${AppRoutePath.ADMIN}/users`,
        openPath: DEFAULT_CLUSTER_ADMIN_URLS.adminUrl,
        children: <SupervisorAccount />,
        isExternal: true,
        onClick: () => {
          openPopup(
            DEFAULT_CLUSTER_ADMIN_URLS.adminUrl,
            getPopupOptions(DEFAULT_CLUSTER_ADMIN_URLS.adminUrl)
          );
        },
      });
    }

    return items;
  }, [
    autoloadedStudiesIds,
    buildAISandboxUrl,
    buildDockedJacketUrl,
    buildJacketUrl,
    buildReporterUrl,
    buildViewerUrl,
    currentCaseId,
    hasAdminAccess,
    isAISandboxEnabled,
    isBlank,
    isJacketOpen,
    isPatientJacketDockedOpen,
    isReporterOnlyExternal,
    isSearchPageEnabled,
    managePatientJacket,
    openedViewerIds,
    priorStudiesIds,
    getPopupOptions,
    shouldPatientJacketBeDocked,
  ]);
};
